import { Layout } from '@/components/layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Instagram, Trophy, Users, Calendar, ArrowRight } from 'lucide-react';

const athletes = [
  {
    id: 1,
    name: 'Puskar Gurung',
    title: 'Professional Coach',
    image: 'https://customer-assets.emergentagent.com/job_nutrasupply/artifacts/i6ayk2y8_IMG_2439.jpeg',
    achievements: ['K11 Certified Trainer', 'Professional Coach', 'Sports Person'],
    instagram: '@bigdawgpuskar05',
    quote: 'NutraFuel has been my trusted supplement partner for 3 years.',
    imageStyle: { objectPosition: 'center 40%' },
  },
  {
    id: 2,
    name: 'James Magar',
    title: 'Fitness Influencer',
    image: 'https://customer-assets.emergentagent.com/job_nutrasupply/artifacts/leyy74s0_IMG_2445.jpeg',
    achievements: ['Fitness Model', 'Champion of U21 Powerlifting', 'Champion of Mr. Kathmandu', 'Brand Ambassador'],
    instagram: '@jamesmagar__',
    quote: 'Finally found a trustworthy supplement store. No more worrying about fake products.',
    imageStyle: { objectPosition: 'top' },
  },
  {
    id: 3,
    name: 'Romika Neupane',
    title: 'Nepal\'s Strongest Female Deadlifter',
    image: 'https://customer-assets.emergentagent.com/job_nutrasupply/artifacts/xrikh08w_IMG_2447.jpeg',
    achievements: ['National Record Holder', 'Natural Powerlifter', 'Woman in Fitness'],
    instagram: '@Romika_neupane',
    quote: 'Competition-level athletes need competition-level supplements.',
    imageStyle: { objectPosition: 'center 35%' },
  },
  {
    id: 4,
    name: 'Susanta Lisham',
    title: 'Fitness Model & Influencer',
    image: 'https://customer-assets.emergentagent.com/job_nutrasupply/artifacts/nocsnx4h_IMG_2453.jpeg',
    achievements: ['Fitness Model', 'Brand Influencer', 'Athlete'],
    instagram: '@susanta_lisham',
    quote: 'Finally found supplements I can trust completely.',
    imageStyle: { objectPosition: 'center 45%' },
  },
  {
    id: 5,
    name: 'Avni Dariyal',
    title: 'Cyclist Asian Champion',
    image: 'https://customer-assets.emergentagent.com/job_nutrasupply/artifacts/z27gj7uf_IMG_2475.jpeg',
    achievements: ['Asian Champion', 'Pro Cyclist'],
    instagram: '@avni_dariyal',
    quote: 'Endurance sports demand pure nutrition. NutraFuel delivers.',
    imageStyle: { objectPosition: 'center 35%' },
  },
  {
    id: 6,
    name: 'Ronit Koirala',
    title: 'Fitness Coach',
    image: 'https://customer-assets.emergentagent.com/job_nutrasupply/artifacts/vnm8be29_IMG_2476.jpeg',
    achievements: ['50+ Transformed', 'Sheru Classic'],
    instagram: '@fit_ronit',
    quote: 'I recommend NutraFuel to all my clients without hesitation.',
    imageStyle: { objectPosition: 'center 35%' },
  },
];

const events = [
  {
    id: 1,
    title: 'North Bengal Bodybuilding Championship',
    date: 'March 2024',
    description: 'Official nutrition partner for the regional championship',
    type: 'Competition',
  },
  {
    id: 2,
    title: 'Siliguri Fitness Expo',
    date: 'January 2024',
    description: 'Product showcase and athlete meet-and-greet',
    type: 'Expo',
  },
  {
    id: 3,
    title: 'Nepal-India CrossFit Challenge',
    date: 'December 2023',
    description: 'Cross-border fitness competition sponsorship',
    type: 'Competition',
  },
];

const AthletesPage = () => {
  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative pt-32 md:pt-40 pb-20 overflow-hidden">
        <div className="absolute inset-0 hero-gradient" />
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDM0djItSDI0di0yaDEyek0zNiAyNHYySDI0di0yaDEyeiIvPjwvZz48L2c+PC9zdmc+')] opacity-30" />
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 text-gold font-semibold text-sm uppercase tracking-wider mb-4">
              <Trophy className="w-4 h-4" />
              ATHLETE COMMUNITY
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold text-primary-foreground leading-tight mb-6">
              Athletes &
              <span className="block text-gold">Collaborations</span>
            </h1>
            <p className="text-lg text-primary-foreground/80 leading-relaxed">
              Trusted by athletes who demand performance, not compromises. Meet the champions who choose NutraFuel.
            </p>
          </div>
        </div>
      </section>

      {/* Athletes Grid */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <span className="inline-block text-gold font-semibold text-sm uppercase tracking-wider mb-4">OUR ATHLETES</span>
            <h2 className="section-title text-foreground">Featured Athletes</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {athletes.map((athlete) => (
              <Card key={athlete.id} className="group overflow-hidden border-0 shadow-soft hover:shadow-elevated transition-all duration-300 hover:-translate-y-2">
                <div className="relative h-72 overflow-hidden">
                  <img
                    src={athlete.image}
                    alt={athlete.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    style={athlete.imageStyle || { objectPosition: 'top' }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-foreground via-foreground/50 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <h3 className="text-xl font-bold text-background mb-1">{athlete.name}</h3>
                    <p className="text-gold font-medium">{athlete.title}</p>
                  </div>
                  <div className="absolute top-4 right-4">
                    <Badge className="bg-primary text-primary-foreground">Partner</Badge>
                  </div>
                </div>
                <CardContent className="p-6">
                  <div className="flex flex-wrap gap-2 mb-4">
                    {athlete.achievements.map((achievement) => (
                      <span key={achievement} className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary">
                        {achievement}
                      </span>
                    ))}
                  </div>
                  <p className="text-sm text-muted-foreground italic mb-4">"{athlete.quote}"</p>
                  <a
                    href={`https://instagram.com/${athlete.instagram.replace('@', '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm text-primary hover:text-gold transition-colors"
                  >
                    <Instagram className="w-4 h-4" />
                    {athlete.instagram}
                  </a>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Events Section */}
      <section className="py-20 bg-muted/50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <span className="inline-block text-gold font-semibold text-sm uppercase tracking-wider mb-4">EVENTS & SPONSORSHIPS</span>
            <h2 className="section-title text-foreground">Community Events</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {events.map((event) => (
              <Card key={event.id} className="bg-card border-0 shadow-soft hover:shadow-elevated transition-all duration-300">
                <CardContent className="p-6">
                  <Badge variant="outline" className="mb-4">{event.type}</Badge>
                  <h3 className="text-lg font-bold text-foreground mb-2">{event.title}</h3>
                  <p className="text-sm text-muted-foreground mb-4">{event.description}</p>
                  <div className="flex items-center gap-2 text-sm text-gold">
                    <Calendar className="w-4 h-4" />
                    {event.date}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Partnership CTA */}
      <section className="py-20 hero-gradient">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <Users className="w-16 h-16 mx-auto text-gold mb-6" />
            <h2 className="text-4xl sm:text-5xl font-display font-bold text-primary-foreground mb-6">
              Become a NutraFuel Athlete
            </h2>
            <p className="text-lg text-primary-foreground/80 mb-8">
              Are you a competitive athlete or fitness influencer? Partner with us and get access to authentic supplements, sponsorship opportunities, and more.
            </p>
            <Button variant="gold" size="xl" asChild>
              <a href="https://wa.me/9779824042067?text=Hi!%20I'm%20interested%20in%20becoming%20a%20NutraFuel%20partner%20athlete" target="_blank" rel="noopener noreferrer">
                Apply for Partnership
                <ArrowRight className="w-5 h-5 ml-2" />
              </a>
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default AthletesPage;
